import { Spin } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import useAuthApp from '../../../hooks/useAuthApp';
import {
  capitalizeFirstLetter,
  generateQueryString,
  IHttpResponse,
} from '@qlibs/react-components';
import { httpRequest } from '../../../helpers/api';
import useReportLog from '../../../hooks/useReportLog';
import {
  EReportDataInterval,
  ILogReportFilter,
} from '../types/report-log.type';
import {
  fetchReportLogBatched,
  mergeReportLogPayloads,
} from '../helpers/reportLogBatch';
import { InfoOutlined } from '@ant-design/icons';
import SummaryTable from '../components/summaryTable';
import ChartRpmFuelSpeedRunning from '../components/charts/chartRpmFuelSpeedRunning';
import ChartFuelCons from '../components/charts/chartFuelCons';
import ChartAERunningTime from '../components/charts/chartAERunningTime';
import PageContent from '../components/dailyReport/pageContent';
import VesselTrackingStatic from '../components/vesselTrackingStatic';
import OperationModePdfSections from '../components/operationModePdfSections';
import useGroup from '../../../zustand/useGroup';
import {
  AssetProperties,
  EAssetType,
  initialAsset,
} from '../../asset/types/asset.type';
import { PdfChartData, ReportPdfData } from '../pdf/reportPdfData';

type DailyReportType = {
  currentPath?: any;
};

type ReportScheduleParams = {
  startAt: number;
  endAt: number;
  interval: any;
  deviceType: string;
  assetId: string;
  timezone: number;
};

const inferAssetTypeFromDeviceType = (deviceType?: string) => {
  const normalized = String(deviceType || '').toLowerCase();
  if (
    normalized.includes('flowmeter') ||
    normalized.includes('fm-1') ||
    normalized.includes('fm-2') ||
    normalized.includes('fm-3')
  ) {
    return EAssetType.FMS;
  }
  if (normalized.includes('rpm') || normalized.includes('ae')) {
    return EAssetType.EMS;
  }
  return EAssetType.FMS;
};

const DailyReport: React.FC<DailyReportType> = ({ currentPath }) => {
  const { doLogin } = useAuthApp();

  const {
    setLoadingLog,
    setLogs,
    loadingLog,
    logs,
    setDistanceUnit,
    setSpeedUnit,
    setCruiseUnit,
  } = useReportLog();
  const { setAsset, setDevices, setFilter, setTimezone } = useGroup();

  /**
   * PDF sync must not depend on GET /asset/:id.
   * Report page loads assets via /asset?filterCompanyIds=..., but PDF only has
   * assetId (often a Ramus2 UUID) which 404s for the temporary PDF token.
   * Seed context from query params; optionally enrich from APIs when available.
   */
  const syncReportContext = async (
    params: ILogReportFilter,
    assetNameFromQuery?: string | null
  ): Promise<AssetProperties> => {
    const displayName = String(assetNameFromQuery || '')
      .replace(/_/g, ' ')
      .trim();

    let asset: AssetProperties = {
      ...initialAsset,
      assetId: params.assetId,
      name: displayName || params.assetId,
      assetType: inferAssetTypeFromDeviceType(params.deviceType),
    };
    setAsset(asset);
    setDevices([]);

    try {
      const assetResponse = await httpRequest.get(`/asset/${params.assetId}`);
      if (assetResponse?.data?.payload) {
        asset = assetResponse.data.payload;
        setAsset(asset);
      }
    } catch (error) {
      console.warn(
        'report-pdf: asset detail unavailable, using query context',
        error
      );
    }

    try {
      const deviceResponse = await httpRequest.get(
        `/device${generateQueryString({
          filterAssetIds: params.assetId,
          filterStatus: 'active',
        })}`
      );
      setDevices(deviceResponse?.data?.payload?.results || []);
    } catch (error) {
      console.warn(
        'report-pdf: device list unavailable, charts will use log payload',
        error
      );
      setDevices([]);
    } finally {
      setTimezone(params.timezone ?? 7);
      setFilter(params);
    }

    return asset;
  };

  const getLogReport = async (
    params: ILogReportFilter,
    operationModeInterval?: EReportDataInterval
  ) => {
    try {
      setLoadingLog(true);

      const shouldBatch = params.interval === EReportDataInterval.minute;
      const requestParams = {
        ...params,
        // Per-window range stays within the default 7-day minute cap when batched.
        ...(params.allowWeeklyMinute ? { allowWeeklyMinute: true } : {}),
      };

      const operationModeParams =
        operationModeInterval === EReportDataInterval.minute &&
        params.deviceType.split(',').includes('operation_mode')
          ? {
              ...requestParams,
              deviceType: 'operation_mode',
              interval: operationModeInterval,
            }
          : undefined;

      const [reportPayload, operationModePayload] = await Promise.all([
        fetchReportLogBatched({
          query: requestParams,
          startAt: Number(params.startAt),
          endAt: Number(params.endAt),
          batch: shouldBatch,
        }),
        operationModeParams
          ? fetchReportLogBatched({
              query: operationModeParams,
              startAt: Number(params.startAt),
              endAt: Number(params.endAt),
              batch: true,
            })
          : Promise.resolve(undefined),
      ]);

      const newResults = mergeReportLogPayloads({}, reportPayload);
      const operationMode =
        operationModePayload?.operation_mode ?? reportPayload.operation_mode;

      if (operationMode && Object.keys(operationMode).length) {
        mergeReportLogPayloads(newResults, {
          operation_mode: operationMode,
        });
      }

      setLogs(newResults);
    } catch (error) {
      console.error('failed get report log', error);
      setLogs({});
    } finally {
      setLoadingLog(false);
    }
  };

  const [deviceType, setDeviceType] = useState<string>('');
  const [assetId, setAssetId] = useState<string>('');
  const [interval, setInterval] = useState<string>('');
  const [startAt, setStartAt] = useState<string>();
  const [endAt, setEndAt] = useState<string>();
  const [reportTimezone, setReportTimezone] = useState<number>(7);
  const [currDataDownloaded, setCurrDataDownloaded] = useState<any>();
  const [operationModeInterval, setOperationModeInterval] = useState<string>();
  const [pdfData, setPdfData] = useState<ReportPdfData>();

  /**
   * Everything this page shows, calculated by the backend with the same code
   * (reportPdfData.ts) from the same log, so the page does not have to load
   * the log. Returns false when it is not available, the page then loads the
   * log and calculates it itself.
   */
  const getPdfData = async (query: Record<string, unknown>) => {
    try {
      setLoadingLog(true);
      const res = await httpRequest.get<IHttpResponse<ReportPdfData>>(
        `report-log/pdf-data${generateQueryString(query)}`
      );
      setPdfData(res.data.payload);
      return true;
    } catch (error) {
      console.warn('report-pdf: server data unavailable, loading the log', error);
      return false;
    } finally {
      setLoadingLog(false);
    }
  };

  async function generateContent() {
    const searchParams = new URLSearchParams(document.location.search);
    const scheduleReport = searchParams.get('scheduleReport');
    const user = searchParams.get('u');
    const pass = searchParams.get('p');
    const assetId = searchParams.get('assetId');
    const assetName = searchParams.get('assetName');
    const startAt = searchParams.get('startAt');
    const endAt = searchParams.get('endAt');
    const deviceType = searchParams.get('deviceType');
    const timezone = searchParams.get('timezone');
    const interval = searchParams.get('interval');
    const operationModeIntervalParam = searchParams.get('operationModeInterval');
    const dataDownloaded = searchParams.get('dataDownloaded');
    const distanceUnit = searchParams.get('distanceUnit');
    const speedUnit = searchParams.get('speedUnit');
    const cruiseUnit = searchParams.get('cruiseUnit');
    const serverData = searchParams.get('serverData') === '1';
    // Legacy flag: enables minute-interval PDF data up to ~1 month on the API.
    const allowWeeklyMinuteParam = searchParams.get('allowWeeklyMinute');
    const allowWeeklyMinute =
      allowWeeklyMinuteParam === 'true' ||
      allowWeeklyMinuteParam === '1';

    setTimeout(() => {
      if (distanceUnit) {
        setDistanceUnit(distanceUnit);
      }
      if (speedUnit) {
        setSpeedUnit(speedUnit);
      }
      if (cruiseUnit) {
        setCruiseUnit(cruiseUnit);
      }
    }, 100);

    if (dataDownloaded) {
      setCurrDataDownloaded(dataDownloaded);
    }
    if (
      scheduleReport &&
      scheduleReport === '1' &&
      assetId &&
      startAt &&
      endAt &&
      deviceType &&
      timezone &&
      interval
    ) {
      setDeviceType(deviceType);
      setAssetId(assetId);
      setInterval(interval);
      setStartAt(startAt);
      setEndAt(endAt);
      setReportTimezone(Number(timezone));
      setOperationModeInterval(operationModeIntervalParam || undefined);
      const reportParams: ILogReportFilter = {
        deviceType,
        assetId,
        interval,
        startAt: Number(startAt),
        endAt: Number(endAt),
        timezone: Number(timezone),
        ...(allowWeeklyMinute ? { allowWeeklyMinute: true } : {}),
      };

      const asset = await syncReportContext(reportParams, assetName);

      if (serverData) {
        const loaded = await getPdfData({
          ...reportParams,
          // a timezone of 0 would be left out of the query string
          timezone: String(reportParams.timezone),
          operationModeInterval: operationModeIntervalParam,
          dataDownloaded,
          speedUnit,
          distanceUnit,
          cruiseUnit,
          assetDeviceIds: (asset.devices || [])
            .map((device) => device.uniqueId || '-')
            .join(','),
          assetType: asset.assetType,
          trackingAssetId: asset.assetId,
        });

        if (loaded) {
          return;
        }
      }

      await getLogReport(
        reportParams,
        operationModeIntervalParam === EReportDataInterval.minute
          ? EReportDataInterval.minute
          : undefined
      );
    } else {
      console.error('something wrong');
    }
  }

  useEffect(() => {
    generateContent();
  }, []);

  const getFlowmeterChild = useMemo(() => {
    let data: string[] = [];

    if (currDataDownloaded) {
      data = currDataDownloaded
        .split(',')
        .filter((item: string) => item.includes('fuel_consumption_running'))
        .map((item: string) => {
          const key = item.split('_');
          return key[0];
        });
    }

    return data;
  }, [currDataDownloaded]);

  const scheduleParams = useMemo<ReportScheduleParams | undefined>(() => {
    if (!startAt || !endAt || !interval || !deviceType || !assetId) {
      return undefined;
    }

    return {
      startAt: Number(startAt),
      endAt: Number(endAt),
      interval,
      deviceType,
      assetId,
      timezone: reportTimezone,
    };
  }, [assetId, deviceType, endAt, interval, reportTimezone, startAt]);

  // console.log('report-daily.deviceType', deviceType);
  // console.log('report-daily.currDataDownloaded', currDataDownloaded);

  if (loadingLog) {
    return (
      <ContainerLoading>
        <LoadingWrapper>
          <Spin />
          <div>Loading data...</div>
        </LoadingWrapper>
      </ContainerLoading>
    );
  } else if (
    (pdfData ? pdfData.hasLogs : logs && Object.keys(logs).length) &&
    currDataDownloaded &&
    currDataDownloaded.length
  ) {
    const charts = pdfData?.charts;
    const fuelCons = (chartType: string): PdfChartData | undefined =>
      charts?.fuelCons[chartType];

    return (
      <React.Fragment>
        {currDataDownloaded.includes('summary') && (
          <PageContent title='Summary'>
            <SummaryTable
              isReportSchedule={true}
              customSummaryProps={{
                startAt: Number(startAt),
                endAt: Number(endAt),
                interval,
              }}
              precomputedSummary={
                pdfData
                  ? {
                      summary: pdfData.summary,
                      gpsSummary: pdfData.gpsSummary,
                      operationMode: pdfData.operationMode,
                      showOperationMode: pdfData.showOperationMode,
                    }
                  : undefined
              }
            />
          </PageContent>
        )}

        {currDataDownloaded.includes('operation_mode') && (
          <OperationModePdfSections
            isReportSchedule={true}
            interval={
              operationModeInterval === EReportDataInterval.minute
                ? operationModeInterval
                : interval
            }
            startAt={Number(startAt)}
            endAt={Number(endAt)}
            precomputed={pdfData?.operationMode}
            hasNextSection={
              currDataDownloaded.includes('vessel_tracking') ||
              currDataDownloaded.includes('rpm_fuel_speed_ae') ||
              currDataDownloaded.includes('me_fuel_consumption') ||
              currDataDownloaded.includes('port_fuel_consumption_running') ||
              currDataDownloaded.includes('starboard_fuel_consumption_running') ||
              currDataDownloaded.includes('center_fuel_consumption_running') ||
              currDataDownloaded.includes('rpm_speed_ae') ||
              currDataDownloaded.includes('ae_fuel_consumption')
            }
          />
        )}

        {currDataDownloaded.includes('vessel_tracking') && (
          <PageContent title='Vessel Tracking'>
            <VesselTrackingStatic
              precomputed={
                pdfData?.vesselTracking && {
                  ...pdfData.vesselTracking,
                  gpsSummary: pdfData.gpsSummary,
                }
              }
            />
          </PageContent>
        )}

        {deviceType &&
          deviceType.split(',').map((item, idx) => {
            if (item === 'flowmeter') {
              return (
                <div key={idx}>
                  {currDataDownloaded.includes('rpm_fuel_speed_ae') && (
                    <PageContent title='RPM vs Fuel Cons. vs Speed vs AE Running Time'>
                      <ChartRpmFuelSpeedRunning
                        isReportSchedule={scheduleParams}
                        precomputed={charts?.rpmFuelSpeed}
                      />
                    </PageContent>
                  )}

                  {currDataDownloaded.includes('me_fuel_consumption') && (
                    <PageContent title='ME Fuel Consumption'>
                      <ChartFuelCons
                        chartType='me'
                        isReportSchedule={scheduleParams}
                        precomputed={fuelCons('me')}
                      />
                    </PageContent>
                  )}

                  {getFlowmeterChild.map((item, idx) => (
                    <PageContent
                      key={idx}
                      title={`${capitalizeFirstLetter(
                        item
                      )} Fuel Cons. vs Running
                            Time`}
                    >
                      <ChartFuelCons
                        chartType={item}
                        isReportSchedule={scheduleParams}
                        precomputed={fuelCons(item)}
                      />
                    </PageContent>
                  ))}
                </div>
              );
            } else if (
              ['rpm'].includes(item) &&
              currDataDownloaded.includes('rpm_speed_ae')
            ) {
              return (
                <PageContent title='RPM vs Speed vs AE Running Time'>
                  <ChartRpmFuelSpeedRunning
                    isReportSchedule={scheduleParams}
                    precomputed={charts?.rpmFuelSpeed}
                  />
                </PageContent>
              );
            }
          })}

        {currDataDownloaded.includes('ae_fuel_consumption') && (
          <PageContent title='AE Running Time'>
            <ChartAERunningTime
              isReportSchedule={scheduleParams}
              precomputed={charts?.aeRunningTime}
            />
          </PageContent>
        )}
      </React.Fragment>
    );
  } else {
    return (
      <ContainerLoading>
        <LoadingWrapper>
          <InfoOutlined />
          <div>Something wrong</div>
        </LoadingWrapper>
      </ContainerLoading>
    );
  }
};

export const ContainerLoading = styled.div`
  background: ${(props) => props.theme.colors.ash300};
  height: calc(100vh);
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: center;

  div {
    margin-top: 10px;
    opacity: 0.5;
    text-align: center;
  }
`;

export const TitleReport = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 60px;
`;

export default DailyReport;
